import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, User, FileText, AlertCircle, Clock, Flag, Tag, Users, Check, Repeat } from 'lucide-react';
import api from '../utils/api';
import RecurringTaskModal from './RecurringTaskModal';


const PersonalTaskModal = ({ 
  isOpen, 
  onClose, 
  onTaskCreated, 
  onTaskUpdated,
  user,
  existingTask = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'Normal',
    status: 'To Do',
    due_date: '',
    due_time: '',
    category: '',
    tags: '',
    assigned_to: [],
    is_recurring: false,
    recurrence_pattern: '',
    notes: ''
  });

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringDescription, setRecurringDescription] = useState('');
  
  // Structured estimated effort state
  const [effortValue, setEffortValue] = useState('');
  const [effortUnit, setEffortUnit] = useState('Hours');

  // Helper: Convert structured format to minutes (for storage)
  const effortToMinutes = (value, unit) => {
    if (!value || isNaN(value)) return '';
    const numValue = parseFloat(value);
    
    const multipliers = {
      'Minutes': 1,
      'Hours': 60,
      'Days': 480 // 8 work hours per day
    };
    
    return String(Math.round(numValue * (multipliers[unit] || 1)));
  };

  useEffect(() => {
    if (isOpen) {
      loadTeamMembers();
      
      if (existingTask) {
        // Populate form with existing task data
        setFormData({
          name: existingTask.name || '',
          description: existingTask.description || '',
          priority: existingTask.priority || 'Normal',
          status: existingTask.status || 'To Do',
          due_date: existingTask.due_date ? existingTask.due_date.split('T')[0] : '',
          due_time: existingTask.due_date ? new Date(existingTask.due_date).toTimeString().slice(0, 5) : '',
          category: existingTask.category || '',
          tags: existingTask.tags ? existingTask.tags.join(', ') : '',
          assigned_to: existingTask.assigned_to || [],
          is_recurring: existingTask.is_recurring || false,
          recurrence_pattern: existingTask.recurrence_pattern || '',
          notes: existingTask.notes || ''
        });
        
        // Use estimated_effort_value and estimated_effort_unit directly from API
        if (existingTask.estimated_effort_value !== null && existingTask.estimated_effort_value !== undefined && existingTask.estimated_effort_unit) {
          setEffortValue(existingTask.estimated_effort_value);
          const unitCapitalized = existingTask.estimated_effort_unit.charAt(0).toUpperCase() + existingTask.estimated_effort_unit.slice(1);
          setEffortUnit(unitCapitalized);
        } else {
          // No effort set - clear the fields
          setEffortValue('');
          setEffortUnit('Hours');
        }
      } else {
        // Reset form for new task
        setFormData({
          name: '',
          description: '',
          priority: 'Normal',
          status: 'To Do',
          due_date: '',
          due_time: '',
          category: '',
          tags: '',
          assigned_to: [user?.id || ''], // Default assign to current user
          is_recurring: false,
          recurrence_pattern: '',
          notes: ''
        });
        
        // Reset structured effort fields
        setEffortValue('');
        setEffortUnit('Hours');
      }
      setErrors({});
    }
  }, [isOpen, existingTask, user?.id]);

  const loadTeamMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/team/members`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleAssigneeChange = (userId, isChecked) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: isChecked 
        ? [...prev.assigned_to, userId]
        : prev.assigned_to.filter(id => id !== userId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Task name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Convert structured effort to minutes for storage
      const estimatedEffortMinutes = effortToMinutes(effortValue, effortUnit);
      
      // Prepare task data
      const taskData = {
        name: formData.name,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        category: formData.category,
        // Send structured effort fields
        estimated_effort_value: effortValue ? parseInt(effortValue, 10) : null,
        estimated_effort_unit: effortUnit ? effortUnit.toLowerCase() : null,
        estimated_effort_minutes: estimatedEffortMinutes ? parseInt(estimatedEffortMinutes, 10) : null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        assigned_to: formData.assigned_to,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        notes: formData.notes,
        is_personal: true // Mark as personal task
      };

      // Combine date and time for due_date
      if (formData.due_date) {
        const dueDateTime = new Date(formData.due_date);
        if (formData.due_time) {
          const [hours, minutes] = formData.due_time.split(':');
          dueDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10));
        }
        taskData.due_date = dueDateTime.toISOString();
      } else {
        taskData.due_date = null;
      }

      let response;
      if (existingTask) {
        // Update existing task
        response = await api.put(`/tasks/${existingTask.id}`, taskData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        onTaskUpdated(response.data);
      } else {
        // Create new task
        response = await api.post(`/tasks`, taskData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        onTaskCreated(response.data);
      }

      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      setErrors({
        submit: error.response?.data?.detail || 'Failed to save task. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRecurringSave = (cronExpression, description) => {
    handleInputChange('recurrence_pattern', cronExpression);
    setRecurringDescription(description);
  };

  const getUserName = (userId) => {
    const member = teamMembers.find(m => m.id === userId || m.user?.id === userId);
    return member ? (member.name || member.user?.name) : 'Unknown User';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <User className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {existingTask ? 'Edit Personal Task' : 'New Personal Task'}
                </h2>
                <p className="text-blue-100 text-sm">Create and manage your personal tasks</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Task Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter task name..."
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    placeholder="Enter task description..."
                  />
                </div>

                {/* Priority and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Flag className="w-4 h-4 inline mr-1" />
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Low">Low</option>
                      <option value="Normal">Normal</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Check className="w-4 h-4 inline mr-1" />
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="To Do">To Do</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Review">Review</option>
                      <option value="Done">Done</option>
                      <option value="On Hold">On Hold</option>
                    </select>
                  </div>
                </div>

                {/* Category and Tags */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Tag className="w-4 h-4 inline mr-1" />
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select category...</option>
                      <option value="Personal">Personal</option>
                      <option value="Learning">Learning</option>
                      <option value="Health">Health</option>
                      <option value="Career">Career</option>
                      <option value="Financial">Financial</option>
                      <option value="Home">Home</option>
                      <option value="Creative">Creative</option>
                      <option value="Social">Social</option>
                      <option value="Travel">Travel</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="important, urgent, review (comma separated)"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Due Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => handleInputChange('due_date', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Due Time
                    </label>
                    <input
                      type="time"
                      value={formData.due_time}
                      onChange={(e) => handleInputChange('due_time', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Estimated Effort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Time Requirement
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={effortValue}
                      onChange={(e) => setEffortValue(e.target.value)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 30"
                    />
                    <select
                      value={effortUnit}
                      onChange={(e) => setEffortUnit(e.target.value)}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="Minutes">Minutes</option>
                      <option value="Hours">Hours</option>
                      <option value="Days">Days</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    How long will this task take? (1 Day = 8 work hours)
                  </p>
                </div>

                {/* Assigned Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="w-4 h-4 inline mr-1" />
                    Assign To
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-3">
                    {teamMembers.map(member => {
                      const memberId = member.id || member.user?.id;
                      const memberName = member.name || member.user?.name;
                      const memberEmail = member.email || member.user?.email;
                      
                      return (
                        <div key={memberId} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`assignee-${memberId}`}
                            checked={formData.assigned_to.includes(memberId)}
                            onChange={(e) => handleAssigneeChange(memberId, e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label 
                            htmlFor={`assignee-${memberId}`}
                            className="text-sm flex items-center space-x-2 cursor-pointer text-gray-900 flex-1"
                          >
                            <span>{memberName} ({memberEmail})</span>
                          </label>
                        </div>
                      );
                    })}
                    {teamMembers.length === 0 && (
                      <p className="text-sm text-gray-500">
                        No team members available to assign
                      </p>
                    )}
                  </div>
                </div>

                {/* Recurring Task */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="is_recurring"
                        checked={formData.is_recurring}
                        onChange={(e) => handleInputChange('is_recurring', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                        Recurring Task
                      </label>
                    </div>
                    
                    {formData.is_recurring && (
                      <button
                        type="button"
                        onClick={() => setShowRecurringModal(true)}
                        className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-sm"
                      >
                        <Repeat className="w-4 h-4" />
                        <span>Configure Schedule</span>
                      </button>
                    )}
                  </div>

                  {formData.is_recurring && recurringDescription && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <Repeat className="w-4 h-4 inline mr-1" />
                        {recurringDescription}
                      </p>
                    </div>
                  )}

                  {formData.is_recurring && !recurringDescription && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-sm text-gray-600">
                        Click "Configure Schedule" to set up advanced recurring options like specific days, times, and end conditions.
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    placeholder="Additional notes or comments..."
                  />
                </div>
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Saving...' : existingTask ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Recurring Task Modal */}
      <RecurringTaskModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSave={handleRecurringSave}
        initialRule={formData.recurrence_pattern}
      />
    </div>
  );
};

export default PersonalTaskModal;